import { LockOutlined, UserOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Form, Input, Select, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import {
    type ChangeUserCandidate,
    listSwitchableUsernames,
    resolveInitialUsername,
} from '../domain/changeUser';
import useQuery from '../hooks/useQuery';

const { Text } = Typography;

interface UsersResponse {
    success: boolean;
    data?: ChangeUserCandidate[];
}

interface ChangeUserFormValues {
    username: string;
    password: string;
}

/**
 * Change User (legacy "Schimbare/Confirmare utilizator").
 *
 * Hands the running workstation session to another staff member, or re-confirms the current
 * one, by picking a user and entering that user's password. The current session is replaced
 * only when the credentials are accepted, so a wrong password leaves the signed-in user intact.
 */
const ChangeUser = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, login } = useAuth();
    const [form] = Form.useForm<ChangeUserFormValues>();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const usersQuery = useQuery<UsersResponse>({ path: '/users' });
    const usernames = useMemo(
        () => listSwitchableUsernames(usersQuery.data?.data ?? []),
        [usersQuery.data],
    );
    const initialUsername = resolveInitialUsername(usernames, user?.username);
    useEffect(() => {
        // The user list arrives after the first render; preselect the signed-in user then,
        // unless the operator has already picked someone.
        if (initialUsername !== undefined && form.getFieldValue('username') === undefined) {
            form.setFieldValue('username', initialUsername);
        }
    }, [form, initialUsername]);
    const usernameOptions = useMemo(
        () => usernames.map(username => ({ value: username, label: username })),
        [usernames],
    );

    const onFinish = async ({ username, password }: ChangeUserFormValues) => {
        setError('');
        setSubmitting(true);
        const result = await login({ username }, password, t('changeUser.success', { username }));
        setSubmitting(false);

        if (result.success) {
            navigate('/');
            return;
        }
        form.setFieldValue('password', '');
        setError(result.error ?? t('changeUser.failed'));
    };

    return (
        <div>
            <PageHeader
                accent="#0f766e"
                icon={<UserSwitchOutlined aria-hidden />}
                title={t('changeUser.title')}
                subtitle={t('changeUser.subtitle')}
            />
            <Card style={{ maxWidth: 440 }} data-testid="change-user-card">
                <Text type="secondary" data-testid="change-user-current">
                    {t('changeUser.currentUser', { username: user?.username ?? '' })}
                </Text>

                {usersQuery.error !== undefined && (
                    <Alert
                        type="error"
                        showIcon
                        message={t('changeUser.loadUsersFailed')}
                        style={{ marginTop: 16 }}
                    />
                )}

                {error && (
                    <Alert
                        type="error"
                        showIcon
                        closable
                        message={t('changeUser.failed')}
                        description={error}
                        onClose={() => setError('')}
                        style={{ marginTop: 16 }}
                        data-testid="change-user-error"
                    />
                )}

                <Form
                    form={form}
                    name="change-user"
                    layout="vertical"
                    size="large"
                    autoComplete="off"
                    onFinish={onFinish}
                    style={{ marginTop: 16 }}
                >
                    <Form.Item
                        name="username"
                        label={t('changeUser.username')}
                        rules={[{ required: true, message: t('changeUser.usernameRequired') }]}
                    >
                        <Select
                            showSearch
                            suffixIcon={<UserOutlined />}
                            loading={usersQuery.loading === true}
                            placeholder={t('changeUser.usernamePlaceholder')}
                            options={usernameOptions}
                            data-testid="change-user-username"
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label={t('changeUser.password')}
                        rules={[{ required: true, message: t('changeUser.passwordRequired') }]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder={t('changeUser.passwordPlaceholder')}
                            autoComplete="off"
                            data-testid="change-user-password"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<UserSwitchOutlined />}
                            loading={submitting}
                            block
                            data-testid="change-user-submit"
                        >
                            {t('changeUser.submit')}
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default ChangeUser;
